import { RELAY_HTTP, getSigned } from "./account";
import { track } from "./analytics";

/**
 * Who the relay knows this phone as. A signed-in phone presents its account token; every other phone presents a guest token the
 * relay issued on first open, so even a guest is a known identity from its very first connection (the relay tells the computer the
 * plan in the join itself: no later answer for the screen to wait on).
 */
const GUEST_TOKEN_KEY = "foreman.guestToken";

let guestToken: string | null | undefined;
function readGuestToken(): string | null {
  if (guestToken !== undefined) return guestToken;
  try {
    const v = localStorage.getItem(GUEST_TOKEN_KEY);
    guestToken = v && /^[0-9a-f]{64}$/.test(v) ? v : null;
  } catch {
    guestToken = null;
  }
  return guestToken;
}

/** The token to connect with now: the sign-in if there is one, else the guest token. Read at every (re)connect. */
export const getIdentityToken = (): string | null => getSigned()?.token ?? readGuestToken();

/** The guest token alone (to tell the relay a computer was removed). */
export const getGuestToken = (): string | null => readGuestToken();

/** Make sure a guest phone has an identity before its first connection. Never blocks the app for long and never throws. */
export async function ensureGuestIdentity(relay = RELAY_HTTP, timeoutMs = 2500): Promise<void> {
  if (!relay || getSigned() || readGuestToken()) return;
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetch(`${relay}/auth/guest`, { method: "POST", signal: ctl.signal });
    const body = (await res.json().catch(() => ({}))) as { token?: string; guestId?: string };
    if (!res.ok || !body.token || !/^[0-9a-f]{64}$/.test(body.token)) return;
    guestToken = body.token;
    try {
      localStorage.setItem(GUEST_TOKEN_KEY, body.token);
    } catch {
      /* private mode: this visit only */
    }
    track("guest_created");
  } catch {
    /* offline or slow: the app starts as an unidentified guest and the next launch tries again */
  } finally {
    clearTimeout(timer);
  }
}

/** The guest chooses this computer as its one; resolves true when the relay accepted it. `computerKey` is the computer's public key (base64), its address at the relay. */
export async function switchGuestComputer(computerKey: string, relay = RELAY_HTTP): Promise<boolean> {
  const token = readGuestToken();
  if (!relay || !token) return false;
  try {
    const res = await fetch(`${relay}/auth/guest/switch`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token, computerId: computerKey }) });
    return res.ok;
  } catch {
    return false;
  }
}

/** A removed computer no longer counts against a guest's one computer. Best effort. */
export function forgetGuestComputer(computerKey: string | undefined, relay = RELAY_HTTP): void {
  const token = readGuestToken();
  if (!relay || !token || !computerKey) return;
  void fetch(`${relay}/auth/guest/forget`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token, computerId: computerKey }) }).catch(() => undefined);
}
